import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest } from "services/api";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import { buildQueryString } from "utils/queryHelpers";

const PaymentMethodContext = createContext();

export const PaymentMethodProvider = ({ children }) => {
  const [paymentMethods, setPaymentMethods] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    filters: {
      show_pos: null, // Filter untuk menampilkan metode pembayaran yang digunakan di POS
      show_reservation: null,
    },
  });

  // ✅ Gunakan useCallback untuk mencegah re-render tidak perlu
  const fetchPaymentMethods = useCallback(async (search = "", page = 1) => {
    setPaymentMethods((prev) => ({ ...prev, loading: true }));

    const queryString = buildQueryString({
      search,
      page,
      limit: 100,
      "filters[show_pos]": filterOptions.filters.show_pos,
      "filters[show_reservation]": filterOptions.filters.show_reservation,
    });

    try {
      const response = await getRequest(`/payment-method${queryString}`);
      if (response.success) {
        setPaymentMethods({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });
      } else {
        throw new Error("Failed to fetch payment methods");
      }
    } catch (error) {
      setPaymentMethods((prev) => ({
        ...prev,
        error: error.message,
        loading: false,
      }));
    }
  }, []);

  // ✅ Gunakan useEffect untuk pemanggilan API hanya saat filterOptions berubah
  useEffect(() => {
    fetchPaymentMethods(filterOptions.search, filterOptions.currentPage);
  }, [fetchPaymentMethods, filterOptions]);

  // ✅ Handle perubahan pencarian dengan debounce
  const handleSearch = (e) => {
    if (e.key === "Enter") {
      const searchValue = e.target.value.trim();
      if (searchValue !== filterOptions.search) {
        setFilterOptions((prev) => ({ ...prev, search: searchValue }));
      }
    }
  };

  // ✅ Handle perubahan halaman
  const handlePageChange = (page) => {
    setFilterOptions((prev) => ({ ...prev, currentPage: page }));
  };

  const Pagination = usePaginationWithComponent({
    totalPages: paymentMethods.pagination?.last_page || 1,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  return (
    <PaymentMethodContext.Provider
      value={{
        paymentMethods,
        handleSearch,
        filterOptions,
        refetch: () =>
          fetchPaymentMethods(filterOptions.search, filterOptions.currentPage),
        Pagination,
        fetchOptions: async (inputValue) => {
          const response = await getRequest(
            `/payment-method?search=${inputValue}`
          );
          return response.success
            ? response.data.map((method) => ({
                value: method.id,
                label: method.name,
              }))
            : [];
        },
      }}
    >
      {children}
    </PaymentMethodContext.Provider>
  );
};

export const usePaymentMethod = () => {
  const context = useContext(PaymentMethodContext);
  if (!context) {
    throw new Error(
      "usePaymentMethod must be used within PaymentMethodProvider"
    );
  }
  return context;
};
